import React, { useState, useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    CircularProgress,
    Chip,
    IconButton,
    Tooltip,
    Tabs,
    Tab,
    useMediaQuery,
    useTheme,
    Divider
} from '@mui/material';
import {
    People,
    Business,
    Subscriptions,
    TrendingUp,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axiosWithAuth from '../utils/axiosWithAuth';
import PlanList from '../components/PlanManagement/PlanList';
import ServiceHealth from '../components/Admin/ServiceHealth';
import AdminQuickView from '../components/Admin/AdminQuickView';

const AdminPage = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // URL-based tab state management
    const getTabFromUrl = () => {
        const tabParam = searchParams.get('tab');
        const tabMap = {
            'cas': 0,
            'users': 1,
            'plans': 2,
            'health': 3
        };
        return tabMap[tabParam] !== undefined ? tabMap[tabParam] : 0;
    };

    const [activeTab, setActiveTab] = useState(0); // Initialize with default, will be set by useEffect
    const [stats, setStats] = useState({
        totalUsers: 0,
        subscriptions: 0,
        activeSubscriptions: 0
    });
    const [users, setUsers] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [openUserDialog, setOpenUserDialog] = useState(false);
    const [openSubscriptionDialog, setOpenSubscriptionDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Form states
    const [userForm, setUserForm] = useState({
        email: '',
        username: '',
        first_name: '',
        last_name: '',
        password: '',
        role: 'user'
    });

    const [subscriptionForm, setSubscriptionForm] = useState({
        plan_id: '',
        owner_id: '',
        status: 'active'
    });

    useEffect(() => {
        fetchData();
    }, []);

    // Initialize tab from URL on component mount
    useEffect(() => {
        const tabFromUrl = getTabFromUrl();
        setActiveTab(tabFromUrl);
    }, []); // Only run on mount

    // Sync URL with tab state
    useEffect(() => {
        const tabNames = ['cas', 'users', 'plans', 'health'];
        const currentTab = tabNames[activeTab];
        if (currentTab) {
            setSearchParams(prev => {
                const newParams = new URLSearchParams(prev);
                newParams.set('tab', currentTab);
                return newParams;
            }, { replace: true });
        }
    }, [activeTab, setSearchParams]);

    // Enhanced security: Check admin role (after all hooks)
    if (user?.role !== 'admin' && user?.role !== 'superadmin') {
        return <Navigate to="/dashboard" replace />;
    }

    const fetchData = async () => {
        try {
            setLoading(true);
            const [statsRes, usersRes, subscriptionsRes] = await Promise.all([
                axiosWithAuth.get('/admin/stats/system'),
                axiosWithAuth.get('/admin/users'),
                axiosWithAuth.get('/admin/subscriptions')
            ]);

            setStats({
                totalUsers: statsRes.data.total_users || 0,
                subscriptions: statsRes.data.total_subscriptions || 0,
                activeSubscriptions: statsRes.data.active_subscriptions || 0
            });

            setUsers(usersRes.data || []);
            setSubscriptions(subscriptionsRes.data || []);
        } catch (error) {
            setError('Failed to fetch data: ' + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async () => {
        try {
            setLoading(true);
            await axiosWithAuth.post('/admin/users', userForm);
            setSuccess('User created successfully!');
            setOpenUserDialog(false);
            setUserForm({
                email: '',
                username: '',
                first_name: '',
                last_name: '',
                password: '',
                role: 'user'
            });
            fetchData();
        } catch (error) {
            setError('Failed to create user: ' + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateUser = async () => {
        try {
            setLoading(true);
            await axiosWithAuth.put(`/admin/users/${selectedUser.id}`, userForm);
            setSuccess('User updated successfully!');
            setOpenUserDialog(false);
            setSelectedUser(null);
            setUserForm({
                email: '',
                username: '',
                first_name: '',
                last_name: '',
                password: '',
                role: 'user'
            });
            fetchData();
        } catch (error) {
            setError('Failed to update user: ' + (error.response?.data?.detail || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteUser = async (userId) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            try {
                setLoading(true);
                await axiosWithAuth.delete(`/admin/users/${userId}`);
                setSuccess('User deleted successfully!');
                fetchData();
            } catch (error) {
                setError('Failed to delete user: ' + (error.response?.data?.detail || error.message));
            } finally {
                setLoading(false);
            }
        }
    };

    // Mobile-friendly user card component
    const UserCard = ({ user }) => (
        <Card sx={{ mb: 2, maxWidth: '100%', overflow: 'hidden' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                            {user.first_name} {user.last_name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ wordBreak: 'break-word' }}>
                            {user.email}
                        </Typography>
                    </Box>
                    <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip
                            label={user.role}
                            color={user.role === 'admin' ? 'primary' : 'default'}
                            size="small"
                        />
                        <Chip
                            label={user.is_active ? 'Active' : 'Inactive'}
                            color={user.is_active ? 'success' : 'error'}
                            size="small"
                        />
                    </Box>
                </Box>

                <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">Username</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {user.username}
                        </Typography>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 1 }} />

                <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center" sx={{ maxWidth: '100%' }}>
                    <Tooltip title="Edit User">
                        <IconButton
                            size="medium"
                            onClick={() => openEditUser(user)}
                        >
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete User">
                        <IconButton
                            size="medium"
                            color="error"
                            onClick={() => handleDeleteUser(user.id)}
                            disabled={user.id === user?.id}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </CardContent>
        </Card>
    );

    const openEditUser = (user) => {
        setSelectedUser(user);
        setUserForm({
            email: user.email,
            username: user.username,
            first_name: user.first_name,
            last_name: user.last_name,
            password: '',
            role: user.role
        });
        setOpenUserDialog(true);
    };

    const openCreateUser = () => {
        setSelectedUser(null);
        setUserForm({
            email: '',
            username: '',
            first_name: '',
            last_name: '',
            password: '',
            role: 'user'
        });
        setOpenUserDialog(true);
    };

    const statsCards = [
        {
            title: 'Total Users',
            value: stats.totalUsers.toString(),
            icon: <People color="primary" />,
            color: '#1976d2',
            tooltip: 'Total number of registered users in the system'
        },
        {
            title: 'Total Subscriptions',
            value: stats.subscriptions.toString(),
            icon: <Subscriptions color="success" />,
            color: '#2e7d32',
            tooltip: 'Total number of subscriptions (active, inactive, cancelled)'
        },
        {
            title: 'Active Subscriptions',
            value: stats.activeSubscriptions.toString(),
            icon: <Business color="secondary" />,
            color: '#dc004e',
            tooltip: 'Number of currently active subscriptions'
        }
    ];

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        // URL will be updated by the useEffect hook
    };

    const renderTabContent = () => {
        return (
            <Box>
                {/* User Management Tab */}
                <Box sx={{ display: activeTab === 0 ? 'block' : 'none' }}>
                    {/* User Management */}
                    <Grid container spacing={3} sx={{ mt: 2 }}>
                        <Grid item xs={12}>
                            <Paper sx={{ p: 3 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="h6">
                                        User Management
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={openCreateUser}
                                        disabled={loading}
                                    >
                                        Create User
                                    </Button>
                                </Box>

                                {isMobile ? (
                                    // Mobile view - Cards
                                    <Box>
                                        {users.map((user) => (
                                            <UserCard key={user.id} user={user} />
                                        ))}
                                    </Box>
                                ) : (
                                    // Desktop view - Table
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Name</TableCell>
                                                    <TableCell>Email</TableCell>
                                                    <TableCell>Username</TableCell>
                                                    <TableCell>Role</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell>Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {users.map((user) => (
                                                    <TableRow key={user.id}>
                                                        <TableCell>
                                                            {user.first_name} {user.last_name}
                                                        </TableCell>
                                                        <TableCell>{user.email}</TableCell>
                                                        <TableCell>{user.username}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={user.role}
                                                                color={user.role === 'admin' ? 'primary' : 'default'}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={user.is_active ? 'Active' : 'Inactive'}
                                                                color={user.is_active ? 'success' : 'error'}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Tooltip title="Edit User">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => openEditUser(user)}
                                                                >
                                                                    <EditIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Delete User">
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => handleDeleteUser(user.id)}
                                                                    disabled={user.id === user?.id}
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>

                {/* Subscription Plans Tab */}
                <Box sx={{ display: activeTab === 1 ? 'block' : 'none' }}>
                    <PlanList />
                </Box>

                {/* Service Health Tab */}
                <Box sx={{ display: activeTab === 2 ? 'block' : 'none' }}>
                    <ServiceHealth />
                </Box>
            </Box>
        );
    };

    if (loading && stats.totalUsers === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" gutterBottom>
                Admin Dashboard
            </Typography>

            {/* Alerts */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            {/* Stats Cards */}
            <Grid container spacing={3}>
                {statsCards.map((stat, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <Tooltip title={stat.tooltip} arrow>
                            <Card>
                                <CardContent>
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Box>
                                            <Typography color="textSecondary" gutterBottom>
                                                {stat.title}
                                            </Typography>
                                            <Typography variant="h4" component="div">
                                                {stat.value}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ color: stat.color }}>
                                            {stat.icon}
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Tooltip>
                    </Grid>
                ))}
            </Grid>

            {/* Admin Quick View */}
            <AdminQuickView />

            {/* Tabs */}
            <Paper sx={{ mt: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant={isMobile ? "scrollable" : "standard"}
                    scrollButtons={isMobile ? "auto" : false}
                    allowScrollButtonsMobile
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        '& .MuiTab-root': {
                            minWidth: { xs: 'auto', sm: 'auto' },
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            padding: { xs: '6px 8px', sm: '12px 16px' }
                        }
                    }}
                >
                    <Tab
                        label={isMobile ? "Users" : "User Management"}
                        icon={<People />}
                        iconPosition="start"
                    />
                    <Tab
                        label={isMobile ? "Plans" : "Subscription Plans"}
                        icon={<SettingsIcon />}
                        iconPosition="start"
                    />
                    <Tab
                        label={isMobile ? "Health" : "Service Health"}
                        icon={<SettingsIcon />}
                        iconPosition="start"
                    />
                </Tabs>
                <Box sx={{ p: 3 }}>
                    {renderTabContent()}
                </Box>
            </Paper>

            {/* User Dialog */}
            <Dialog
                open={openUserDialog}
                onClose={() => setOpenUserDialog(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle>
                    {selectedUser ? 'Edit User' : 'Create New User'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="First Name"
                                value={userForm.first_name}
                                onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Last Name"
                                value={userForm.last_name}
                                onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                value={userForm.email}
                                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Username"
                                value={userForm.username}
                                onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Password"
                                type="password"
                                value={userForm.password}
                                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                                helperText={selectedUser ? "Leave blank to keep current password" : ""}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                select
                                label="Role"
                                value={userForm.role}
                                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                            >
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                                <option value="superadmin">Super Admin</option>
                            </TextField>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenUserDialog(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={selectedUser ? handleUpdateUser : handleCreateUser}
                        variant="contained"
                        disabled={loading}
                    >
                        {selectedUser ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminPage; 